import { useState } from "react";
import { Lock, Eye, EyeOff } from "lucide-react";

/**
 * SignupForm - Registration form with GDPR consent
 */
export default function SignupForm({ 
  email, 
  onSubmit, 
  onBack, 
  loading,
  error 
}) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // GDPR Consent state
  const [consentTerms, setConsentTerms] = useState(false);
  const [consentDataSharing, setConsentDataSharing] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!password || password.length < 6) return;
    if (password !== confirmPassword) return;
    if (!consentTerms || !consentDataSharing) return;
    
    onSubmit({ 
      password, 
      confirmPassword,
      consentTerms,
      consentDataSharing,
      consentMarketing
    });
  };

  const handleReset = () => {
    setPassword("");
    setConfirmPassword("");
    setConsentTerms(false);
    setConsentDataSharing(false);
    setConsentMarketing(false);
    onBack();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-3 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-700">
          Create an account for <strong>{email}</strong>
        </p>
      </div>
      
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}
      
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Create a password"
          className="w-full pl-10 pr-12 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
          data-testid="modal-new-password-input"
          required
          minLength={6}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>
      
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type={showPassword ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm password"
          className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand"
          data-testid="modal-confirm-password-input"
          required
        />
      </div>
      
      {/* GDPR Consent Checkboxes */}
      <div className="space-y-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
        <p className="text-xs font-medium text-gray-700 uppercase tracking-wide">Data Protection Consent</p>
        
        {/* Required: Terms & Conditions */}
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={consentTerms}
            onChange={(e) => setConsentTerms(e.target.checked)}
            className="mt-0.5 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            required
            data-testid="consent-terms"
          />
          <span className="text-sm text-gray-600 group-hover:text-gray-900">
            I agree to the{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline font-medium">
              Terms & Conditions
            </a>{" "}
            and{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline font-medium">
              Privacy Policy
            </a>
            <span className="text-red-500 ml-1">*</span>
          </span>
        </label>
        
        {/* Required: Data Sharing Consent */}
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={consentDataSharing}
            onChange={(e) => setConsentDataSharing(e.target.checked)}
            className="mt-0.5 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            required
            data-testid="consent-data-sharing"
          />
          <span className="text-sm text-gray-600 group-hover:text-gray-900">
            I consent to my <strong>anonymised</strong> contribution data (reviews, WiFi tests, venue info) 
            being shared with third parties for analytical purposes
            <span className="text-red-500 ml-1">*</span>
          </span>
        </label>
        
        {/* Optional: Marketing */}
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={consentMarketing}
            onChange={(e) => setConsentMarketing(e.target.checked)}
            className="mt-0.5 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            data-testid="consent-marketing"
          />
          <span className="text-sm text-gray-600 group-hover:text-gray-900">
            I would like to receive updates, tips, and promotional offers from Strider (optional)
          </span>
        </label>
        
        <p className="text-xs text-gray-500 mt-2">
          <span className="text-red-500">*</span> Required. Withdraw consent anytime via{" "}
          <a href="mailto:privacy@strider.work" className="text-teal-600 hover:underline">privacy@strider.work</a>
        </p>
      </div>
      
      <button
        type="submit"
        disabled={loading || !consentTerms || !consentDataSharing}
        className="w-full py-3 bg-brand text-white rounded-xl font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="modal-register-btn"
      >
        {loading ? "Creating account..." : "Create Account & Claim Points"}
      </button>
      
      <button
        type="button"
        onClick={handleReset}
        className="w-full text-gray-500 hover:text-gray-700 text-sm"
      >
        ← Use a different email
      </button>
    </form>
  );
}
